// Bilingual copy for the entire Übergabeprotokoll flow.
// English is the default; switches via the existing LanguageContext.
// All protocol surfaces (entry + 5 steps) read from this dictionary so a
// single language toggle flips the whole flow.

export type ProtocolLang = "en" | "de";

export const PROTOCOL_COPY = {
  en: {
    // Entry
    greeting: "Hi",
    title: "Handover protocol",
    subtitle:
      "Document the state of your room and shared areas in just a few steps.",
    bulletDuration: "Takes about 8–12 min",
    bulletPause: "You can pause anytime",
    bulletStorage: "Photos are saved securely",
    yourHome: "Your home",
    fallbackHome: "Your coliviq home",
    room: "Room",
    noBooking: "No active booking found. Please contact support.",
    kindMoveIn: "Move-in",
    kindMoveInSub: "Fill in when you move in",
    kindMoveOut: "Move-out",
    kindMoveOutSub: "Fill in before you leave",
    badgeRecommended: "RECOMMENDED TODAY",
    pauseHint: "You can pause anytime — your progress is saved.",
    cta: "Start now",
    errorCreate: "Couldn't create protocol",

    // Top strip
    pause: "Pause",
    back: "Back",
    continue: "Continue",
    step: "Step",
    of: "of",
    yourProgress: "Your progress",
    sectionsDone: "{done}/{total} sections done",

    // Steps
    stepKeys: "Keys",
    stepPhotos: "Photos",
    stepInventory: "Inventory",
    stepCommon: "Common areas",
    stepSign: "Signature",

    // Photos step
    photosTitle: "Photos of the room",
    photosSubtitle:
      "Photograph all walls, floor, windows and the ceiling. Minimum {min} photos.",
    photosCount: "{n}/{max} photos",
    photosMin: "min. {n} required",
    photosCta: "Continue to inventory",
    continueHintPhotos: "Upload {n} more photo(s) of the room to continue.",
    continueHintKeys: "Upload at least {n} key photo to continue.",

    // Inventory step
    inventoryTitle: "Check inventory",
    inventorySubtitle: "Photo and condition are required for every item.",
    conditionGood: "Good",
    conditionWear: "Signs of wear",
    conditionDamaged: "Damaged",
    conditionLabel: "Condition",
    conditionChoose: "Choose",
    inventoryCta: "Continue to common areas",
    missingConditions: "{n} conditions missing",
    missingPhotos: "{n} photo(s) still missing",
    inventoryCheckedCount: "{done}/{total} checked",
    defectDescription: "Description of damage",
    severityLow: "Low",
    severityMedium: "Medium",
    severityHigh: "Serious",

    // Common areas step
    commonTitle: "Common areas",
    commonSubtitle:
      "At least {min} photos per area — photograph all visible damage.",
    commonAreaKitchen: "Kitchen",
    commonAreaBath: "Bathroom",
    commonAreaHallway: "Hallway",
    commonAreaOther: "Other",
    reportDefect: "Report damage",
    commonAreaCount: "{n}/{total} areas done",
    commonCta: "Continue to signature",
    continueHintCommon: "Upload {n} more photo(s) to {area} to continue.",
    reportedDefects: "Reported damage",

    // Defect sheet
    reportDamageTitle: "Report damage",
    photoOfDamage: "Photo of the damage",
    capturePhoto: "Take a photo",
    description: "Description",
    descriptionPlaceholder: "e.g. scratch on the desk surface, ~10 cm long.",
    severity: "Severity",
    cancel: "Cancel",
    saveDamage: "Save damage",
    saveFailed: "Save failed",
    describeShort: "Please enter a short description.",

    // Sign step
    signTitle: "Confirmation",
    signSubtitle:
      "Review the details and sign digitally to finalize the protocol.",
    summary: "Summary",
    object: "Property",
    roomLabel: "Room",
    date: "Date",
    tenant: "Tenant",
    operation: "Operation",
    moveIn: "Move-in",
    moveOut: "Move-out",
    signature: "Signature",
    clear: "Clear",
    signTerms:
      "By signing you confirm that all details are correct. coliviq and the landlord receive a copy by email. Terms of service and the privacy policy apply.",
    finalizeCta: "Finalize protocol",
    pleaseSign: "Please sign to continue.",

    // Done
    doneTitle: "Protocol submitted",
    doneSubtitle:
      "Your handover protocol has been saved. We've emailed a copy to:",
    refLabel: "REF",
    downloadPdf: "Download PDF",
    pdfSoon: "PDF export is coming soon.",
    pdfSoonDesc: "We'll notify you as soon as the download is available.",
    toHome: "Back to dashboard",

    // Misc
    notFound: "Protocol not found.",
    photoUploadFailed: "Photo couldn't be uploaded",
    photoDeleteFailed: "Photo couldn't be deleted",
    tipStart: "Tip before you start",
    tipBody:
      "Make sure you have good lighting and check your storage — each photo ~2 MB.",
    activeBadge: "Active",
    doneBadge: "Done",

    // Protocol status / history
    statusSubmitted: "Submitted",
    statusDraft: "In progress",
    statusNotStarted: "Not started yet",
    statusLocked: "Available later",
    statusExpired: "Auto-accepted",
    statusClosed: "Closed",
    submittedOn: "Submitted on {date}",
    lockedUntil: "Available from {date}",
    lockedHint: "Move-out becomes available 14 days before you leave.",
    moveInLockedHint:
      "Move-in protocol becomes available on your move-in day and can be filled in for 48 hours.",
    moveInExpiredHint:
      "The 48-hour window closed; the room is deemed accepted as per your rental contract.",
    moveOutExpiredHint:
      "The move-out window closed without a completed protocol.",
    closedHint: "The protocol is closed for you at this time.",
    viewPdf: "View PDF",
    viewPdfFailed: "Could not open the PDF. Try again in a moment.",
    loadProtocolFailed:
      "We couldn't load your saved protocol. Check your connection and try again.",
    noPdfAvailable: "No PDF on file",
    resumeDraft: "Resume",
    startMoveIn: "Start move-in protocol",
    startMoveOut: "Start move-out protocol",
    // "Action needed" nudge (COL-368) — shown from the day before move-in /
    // seven days before move-out until the protocol is submitted.
    actionNeeded: "Action needed",
    actionNeededMoveInHint: "Fill in your move-in protocol · move-in on {date}",
    actionNeededMoveOutHint:
      "Fill in your move-out protocol · move-out on {date}",
    actionNeededMoveInHintNoDate: "Fill in your move-in protocol",
    actionNeededMoveOutHintNoDate: "Fill in your move-out protocol",
    actionNeededUpcomingHint:
      "Opens on {date} — fill it in on your move-in day",
    actionNeededAria: "Handover protocol needs your attention",
    bothDone: "You're all set! Both protocols are submitted.",
    moveInDoneTitle: "Move-in protocol",
    moveOutDoneTitle: "Move-out protocol",
    nextStep: "Next",
    refLabelInline: "Ref",
    doneTitleKind: "{kind} protocol submitted",
  },
  de: {
    greeting: "Hallo",
    title: "Übergabeprotokoll",
    subtitle:
      "Dokumentiere den Zustand deines Zimmers und der Gemeinschaftsbereiche in wenigen Schritten.",
    bulletDuration: "Dauert ca. 8–12 Min",
    bulletPause: "Du kannst jederzeit pausieren",
    bulletStorage: "Fotos werden sicher gespeichert",
    yourHome: "Dein Zuhause",
    fallbackHome: "Dein coliviq Zuhause",
    room: "Zimmer",
    noBooking: "Keine aktive Buchung gefunden. Bitte kontaktiere den Support.",
    kindMoveIn: "Einzug",
    kindMoveInSub: "Beim Bezug ausfüllen",
    kindMoveOut: "Auszug",
    kindMoveOutSub: "Beim Verlassen ausfüllen",
    badgeRecommended: "EMPFOHLEN HEUTE",
    pauseHint:
      "Du kannst jederzeit pausieren — dein Fortschritt bleibt gespeichert.",
    cta: "Jetzt starten",
    errorCreate: "Protokoll konnte nicht erstellt werden",

    pause: "Pause",
    back: "Zurück",
    continue: "Weiter",
    step: "Schritt",
    of: "von",
    yourProgress: "Dein Fortschritt",
    sectionsDone: "{done}/{total} Abschnitte fertig",

    stepKeys: "Schlüssel",
    stepPhotos: "Fotos",
    stepInventory: "Inventar",
    stepCommon: "Gemeinschaft",
    stepSign: "Unterschrift",

    photosTitle: "Fotos vom Raum",
    photosSubtitle:
      "Fotografiere alle Wände, Boden, Fenster und die Decke. Mindestens {min} Fotos.",
    photosCount: "{n}/{max} Fotos",
    photosMin: "min. {n} erforderlich",
    photosCta: "Weiter zur Inventarliste",
    continueHintPhotos: "Lade noch {n} Foto(s) vom Raum hoch, um fortzufahren.",
    continueHintKeys:
      "Lade mindestens {n} Schlüsselfoto hoch, um fortzufahren.",

    inventoryTitle: "Inventar prüfen",
    inventorySubtitle:
      "Foto und Zustand sind für jeden Gegenstand verpflichtend.",
    conditionGood: "Gut",
    conditionWear: "Gebrauchsspuren",
    conditionDamaged: "Beschädigt",
    conditionLabel: "Zustand",
    conditionChoose: "Wählen",
    inventoryCta: "Weiter zu Gemeinschaftsbereichen",
    missingConditions: "{n} Zustand-Angaben fehlen",
    missingPhotos: "{n} Foto(s) fehlen noch",
    inventoryCheckedCount: "{done}/{total} geprüft",
    defectDescription: "Beschreibung des Mangels",
    severityLow: "Gering",
    severityMedium: "Mittel",
    severityHigh: "Schwerwiegend",

    commonTitle: "Gemeinschaftsbereiche",
    commonSubtitle:
      "Mindestens {min} Fotos pro Bereich – fotografiere alle sichtbaren Mängel.",
    commonAreaKitchen: "Küche",
    commonAreaBath: "Bad",
    commonAreaHallway: "Flur",
    commonAreaOther: "Sonstiges",
    reportDefect: "Mangel melden",
    commonAreaCount: "{n}/{total} Bereiche fertig",
    commonCta: "Weiter zur Unterschrift",
    continueHintCommon:
      "Lade noch {n} Foto(s) für {area} hoch, um fortzufahren.",
    reportedDefects: "Gemeldete Mängel",

    reportDamageTitle: "Mangel melden",
    photoOfDamage: "Foto des Mangels",
    capturePhoto: "Foto aufnehmen",
    description: "Beschreibung",
    descriptionPlaceholder:
      "z. B. Kratzer auf der Tischplatte, ca. 10 cm lang.",
    severity: "Schweregrad",
    cancel: "Abbrechen",
    saveDamage: "Mangel speichern",
    saveFailed: "Speichern fehlgeschlagen",
    describeShort: "Bitte gib eine kurze Beschreibung ein.",

    signTitle: "Bestätigung",
    signSubtitle:
      "Prüfe die Angaben und unterschreibe digital, um das Protokoll abzuschließen.",
    summary: "Übersicht",
    object: "Objekt",
    roomLabel: "Zimmer",
    date: "Datum",
    tenant: "Mieter:in",
    operation: "Vorgang",
    moveIn: "Einzug",
    moveOut: "Auszug",
    signature: "Unterschrift",
    clear: "Löschen",
    signTerms:
      "Mit deiner Unterschrift bestätigst du, dass alle Angaben korrekt sind. coliviq und der/die Vermieter:in erhalten eine Kopie per E-Mail. Es gelten die AGB und die Datenschutzerklärung.",
    finalizeCta: "Protokoll abschließen",
    pleaseSign: "Bitte unterschreibe, um fortzufahren.",

    doneTitle: "Protokoll eingereicht",
    doneSubtitle:
      "Dein Übergabeprotokoll wurde gespeichert. Eine Kopie haben wir dir per E-Mail geschickt an:",
    refLabel: "REF",
    downloadPdf: "PDF herunterladen",
    pdfSoon: "PDF-Export folgt in Kürze.",
    pdfSoonDesc: "Wir benachrichtigen dich, sobald der Download verfügbar ist.",
    toHome: "Zum Dashboard",

    notFound: "Protokoll nicht gefunden.",
    photoUploadFailed: "Foto konnte nicht hochgeladen werden",
    photoDeleteFailed: "Foto konnte nicht gelöscht werden",
    tipStart: "Tipp vor dem Start",
    tipBody:
      "Sorge für gutes Licht und prüfe deinen Speicherplatz – jedes Foto ca. 2 MB.",
    activeBadge: "Aktiv",
    doneBadge: "Fertig",

    statusSubmitted: "Eingereicht",
    statusDraft: "In Bearbeitung",
    statusNotStarted: "Noch nicht gestartet",
    statusLocked: "Später verfügbar",
    statusExpired: "Automatisch angenommen",
    statusClosed: "Geschlossen",
    submittedOn: "Eingereicht am {date}",
    lockedUntil: "Ab {date} verfügbar",
    lockedHint: "Auszug-Protokoll wird 14 Tage vor deinem Auszug verfügbar.",
    moveInLockedHint:
      "Das Einzug-Protokoll wird an deinem Einzugstag verfügbar und kann 48 Stunden lang ausgefüllt werden.",
    moveInExpiredHint:
      "Das 48-Stunden-Fenster ist geschlossen; das Zimmer gilt gemäß deinem Mietvertrag als angenommen.",
    moveOutExpiredHint:
      "Das Auszug-Fenster wurde ohne ausgefülltes Protokoll geschlossen.",
    closedHint: "Das Protokoll ist für dich derzeit geschlossen.",
    viewPdf: "PDF ansehen",
    viewPdfFailed:
      "Das PDF konnte nicht geöffnet werden. Bitte versuche es gleich noch einmal.",
    loadProtocolFailed:
      "Dein gespeichertes Protokoll konnte nicht geladen werden. Prüfe deine Verbindung und versuche es erneut.",
    noPdfAvailable: "Kein PDF vorhanden",
    resumeDraft: "Fortsetzen",
    startMoveIn: "Einzug-Protokoll starten",
    startMoveOut: "Auszug-Protokoll starten",
    actionNeeded: "Handlung erforderlich",
    actionNeededMoveInHint:
      "Fülle dein Einzug-Protokoll aus · Einzug am {date}",
    actionNeededMoveOutHint:
      "Fülle dein Auszug-Protokoll aus · Auszug am {date}",
    actionNeededMoveInHintNoDate: "Fülle dein Einzug-Protokoll aus",
    actionNeededMoveOutHintNoDate: "Fülle dein Auszug-Protokoll aus",
    actionNeededUpcomingHint:
      "Öffnet am {date} – fülle es an deinem Einzugstag aus",
    actionNeededAria: "Übergabeprotokoll: Handlung erforderlich",
    bothDone: "Alles erledigt! Beide Protokolle sind eingereicht.",
    moveInDoneTitle: "Einzug-Protokoll",
    moveOutDoneTitle: "Auszug-Protokoll",
    nextStep: "Weiter",
    refLabelInline: "Ref",
    doneTitleKind: "{kind}-Protokoll eingereicht",
  },
} as const;

// Union of both language shapes — with `as const`, en/de have different
// literal types, so accessing PROTOCOL_COPY[lang] returns the union. Using
// the union here keeps assignments and reads compatible across languages.
export type ProtocolCopy = {
  [K in keyof (typeof PROTOCOL_COPY)["en"]]: string;
};

export function formatMsg(
  template: string,
  values: Record<string, string | number>
): string {
  return template.replace(/\{(\w+)\}/g, (_, k) => String(values[k] ?? ""));
}
